import type { HistoryPage } from '../history/list.ts';
import { isVideoId } from '../links/routes.ts';
import { parseError, type ParseError } from '../result.ts';

/**
 * `GET /api/v1/auth/history` → a bare array of id strings, parsed under R4's drop-malformed-items
 * discipline. `history/list.ts` carries the measurements that make the paging safe; this is the
 * three-line half that turns a parsed value into a page.
 *
 * **An entry is validated with {@link isVideoId} rather than merely being a non-empty string**, which is
 * a deliberate reach beyond "is it the right JSON type". The id is the row's *entire* content: it is the
 * thumbnail URL, the hydration request and the watch link, all three derived from it. An id `parseRoute`
 * would not accept as a bare video id is a row that cannot be opened, cannot be hydrated, and whose
 * thumbnail request **never answers** — measured for a well-shaped id with no video behind it
 * (`/vi/aaaaaaaaaaa/mqdefault.jpg` returned nothing at all; `curl` timed out at 25 s, twice, with no
 * status and no error). A row like that would sit in the grid as a permanently pending image, and
 * `onError` never fires to draw the empty frame. So it is dropped and counted.
 *
 * The strictness leans on the write side agreeing, which was measured rather than assumed: the instance
 * validates the *shape* and nothing else. `POST /api/v1/auth/history/x` and a 300-character id both
 * answer `400 {"error":"Invalid video id."}`, so every id that can get into a history matches this
 * pattern. What the instance does **not** validate is existence — `history/list.ts`'s
 * `markWatchedPath` has that measurement, and it is why a *well-shaped* id with no video is a normal
 * row rather than a parse failure.
 *
 * `JSON.parse` happens in the platform (R4), so `raw` is already a value.
 */
export function parseHistoryPage(raw: unknown): HistoryPage {
  if (!Array.isArray(raw)) {
    return { ids: [], dropped: [parseError('history', null, 'wrong-type')], malformed: true };
  }

  const ids: string[] = [];
  const dropped: ParseError[] = [];
  for (const entry of raw as readonly unknown[]) {
    if (typeof entry !== 'string') {
      dropped.push(parseError('history-entry', null, 'wrong-type'));
      continue;
    }
    if (!isVideoId(entry)) {
      dropped.push(parseError('history-entry', null, 'malformed'));
      continue;
    }
    ids.push(entry);
  }

  return { ids, dropped, malformed: false };
}
